import { createContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import type { DisplayFeedback } from '../api/analysis';
import type { QuestionState } from '../pages/questionSet/QuestionSessionTypes';
import { type SessionType, type FocusedSessionKind, type QuestionSessionConfig, getSessionConfig } from '../config/questionSessionConfig';

// Refresh persistence — only the navigation skeleton (ids + index + return
// path + type). per-question state and feedback cache are not persisted; on
// reload they refetch as needed. sessionStorage matches the intended TTL:
// the practice session ends with the tab.
const STORAGE_KEY = 'practiceSession';

type PersistedSession = {
  questionIds: string[];
  subjectId: string | null;
  returnPath: string | null;
  currentIndex: number;
  sessionType: SessionType;
  /** Focused chrome for this session (e.g. 'quiz' = placement). Absent for
   *  ordinary practice/review sessions. */
  focusedKind?: FocusedSessionKind | null;
  /** Test-session id a review was built from. Persisted because the feedback
   *  cache and per-question states below are NOT — this is what lets a reload
   *  re-fetch them instead of rendering submitted work as "not attempted". */
  reviewSessionId?: string | null;
};

function readPersisted(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!Array.isArray(parsed.questionIds) || parsed.questionIds.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

export interface QuestionSessionContextType {
  questionIds: string[];
  subjectId: string | null;
  returnPath: string | null;
  currentIndex: number;
  hasSession: boolean;
  sessionType: SessionType;
  /** Focused chrome kind for this session ('quiz' = placement), or null for an
   *  ordinary session. Drives the placement solve chrome on the normal
   *  /question/:id route — see useSessionSource. */
  focusedKind: FocusedSessionKind | null;
  /** Set only for review sessions — see PersistedSession.reviewSessionId. */
  reviewSessionId: string | null;
  config: QuestionSessionConfig;
  startSession: (questionIds: string[], subjectId: string | null, initialIndex: number, returnPath?: string, sessionType?: SessionType, focusedKind?: FocusedSessionKind | null, reviewSessionId?: string | null) => void;
  clearSession: () => void;
  goToIndex: (i: number) => number | false;
  goToNext: () => number | false;
  goToPrev: () => number | false;
  saveQuestionState: (questionId: string, state: QuestionState) => void;
  getQuestionState: (questionId: string) => QuestionState | null;
  cacheFeedback: (questionId: string, feedback: DisplayFeedback) => void;
  getCachedFeedback: (questionId: string) => DisplayFeedback | null;
  clearCachedFeedback: (questionId: string) => void;
  bulkCacheFeedback: (entries: Map<string, DisplayFeedback>) => void;
  /** Track which questions have been visited during this session */
  markVisited: (questionId: string) => void;
  visitedIds: Set<string>;
}

export const QuestionSessionContext = createContext<QuestionSessionContextType | null>(null);

export function QuestionSessionProvider({ children }: { children: ReactNode }) {
  const persisted = useMemo(() => readPersisted(), []);
  const [questionIds, setQuestionIds] = useState<string[]>(persisted?.questionIds ?? []);
  const [subjectId, setSubjectId] = useState<string | null>(persisted?.subjectId ?? null);
  const [returnPath, setReturnPath] = useState<string | null>(persisted?.returnPath ?? null);
  const [currentIndex, setCurrentIndex] = useState(persisted?.currentIndex ?? 0);
  const [sessionType, setSessionType] = useState<SessionType>(persisted?.sessionType ?? 'practice');
  const [focusedKind, setFocusedKind] = useState<FocusedSessionKind | null>(persisted?.focusedKind ?? null);
  const [reviewSessionId, setReviewSessionId] = useState<string | null>(persisted?.reviewSessionId ?? null);
  const [questionStates] = useState(() => new Map<string, QuestionState>());
  const [feedbackCache] = useState(() => new Map<string, DisplayFeedback>());
  const [visitedIds, setVisitedIds] = useState(() => new Set<string>());

  // Mirror the navigation skeleton to sessionStorage so refresh restores it.
  useEffect(() => {
    if (questionIds.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const payload: PersistedSession = { questionIds, subjectId, returnPath, currentIndex, sessionType, focusedKind, reviewSessionId };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Quota / private-mode failures are non-fatal — refresh just won't restore.
    }
  }, [questionIds, subjectId, returnPath, currentIndex, sessionType, focusedKind, reviewSessionId]);

  const hasSession = questionIds.length > 0;
  const config = useMemo(() => getSessionConfig(sessionType), [sessionType]);

  const startSession = useCallback((ids: string[], sid: string | null, initialIndex: number, rPath?: string, sType?: SessionType, fKind?: FocusedSessionKind | null, rSessionId?: string | null) => {
    setQuestionIds(ids);
    setSubjectId(sid);
    setReturnPath(rPath ?? null);
    setCurrentIndex(initialIndex);
    setSessionType(sType ?? 'practice');
    setFocusedKind(fKind ?? null);
    setReviewSessionId(rSessionId ?? null);
    questionStates.clear();
    feedbackCache.clear();
    setVisitedIds(new Set());
  }, [questionStates, feedbackCache]);

  const clearSession = useCallback(() => {
    setQuestionIds([]);
    setSubjectId(null);
    setReturnPath(null);
    setCurrentIndex(0);
    setSessionType('practice');
    setFocusedKind(null);
    setReviewSessionId(null);
    questionStates.clear();
    feedbackCache.clear();
    setVisitedIds(new Set());
  }, [questionStates, feedbackCache]);

  const goToIndex = useCallback((i: number): number | false => {
    if (i < 0 || i >= questionIds.length) return false;
    setCurrentIndex(i);
    return i;
  }, [questionIds.length]);

  const goToNext = useCallback((): number | false => {
    const next = currentIndex + 1;
    if (next >= questionIds.length) return false;
    setCurrentIndex(next);
    return next;
  }, [currentIndex, questionIds.length]);

  const goToPrev = useCallback((): number | false => {
    const prev = currentIndex - 1;
    if (prev < 0) return false;
    setCurrentIndex(prev);
    return prev;
  }, [currentIndex]);

  const saveQuestionState = useCallback((questionId: string, state: QuestionState) => {
    questionStates.set(questionId, state);
  }, [questionStates]);

  const getQuestionState = useCallback((questionId: string): QuestionState | null => {
    return questionStates.get(questionId) ?? null;
  }, [questionStates]);

  const cacheFeedback = useCallback((questionId: string, feedback: DisplayFeedback) => {
    feedbackCache.set(questionId, feedback);
  }, [feedbackCache]);

  const getCachedFeedback = useCallback((questionId: string): DisplayFeedback | null => {
    return feedbackCache.get(questionId) ?? null;
  }, [feedbackCache]);

  const clearCachedFeedback = useCallback((questionId: string) => {
    feedbackCache.delete(questionId);
  }, [feedbackCache]);

  const bulkCacheFeedback = useCallback((entries: Map<string, DisplayFeedback>) => {
    for (const [qId, fb] of entries) {
      feedbackCache.set(qId, fb);
    }
  }, [feedbackCache]);

  const markVisited = useCallback((questionId: string) => {
    setVisitedIds(prev => {
      if (prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.add(questionId);
      return next;
    });
  }, []);

  return (
    <QuestionSessionContext.Provider
      value={{
        questionIds,
        subjectId,
        returnPath,
        currentIndex,
        hasSession,
        sessionType,
        focusedKind,
        reviewSessionId,
        config,
        startSession,
        clearSession,
        goToIndex,
        goToNext,
        goToPrev,
        saveQuestionState,
        getQuestionState,
        cacheFeedback,
        getCachedFeedback,
        clearCachedFeedback,
        bulkCacheFeedback,
        markVisited,
        visitedIds,
      }}
    >
      {children}
    </QuestionSessionContext.Provider>
  );
}
